import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  course_fee_list,
  course_fee_quote,
  course_fee_remove,
  course_fee_save,
} from "../../../apis/Course";

// Fee schedule editor for a course.
//
// Fees are not a single number that gets overwritten. Each row says "from this
// year and term onward, the course costs this", so putting a price up next
// term leaves every earlier term charging what it always charged. The newest
// schedule that is not after the term being quoted is the one that applies.
//
// The calculator lives on the server (utils/FeeCalculator.js) — the preview
// below calls it rather than repeating the arithmetic here, so admin can never
// quote a different number to the one a parent is billed.

const BRAND = "#A6192E";

const s = {
  wrap: { marginTop: 8 },
  intro: { fontSize: 13, color: "#6b7280", margin: "0 0 16px", lineHeight: 1.6 },
  panel: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "18px 18px 20px",
    marginBottom: 18,
  },
  panelTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1f2937",
    margin: "0 0 14px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
  },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: "#4b5563" },
  hint: { fontSize: 11, color: "#9ca3af" },
  input: {
    height: 38,
    borderRadius: 6,
    border: "1px solid #d1d5db",
    padding: "0 10px",
    fontSize: 14,
    width: "100%",
  },
  btn: {
    background: BRAND,
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "9px 20px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnGhost: {
    background: "#fff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnLink: {
    background: "none",
    border: "none",
    color: BRAND,
    fontSize: 12,
    cursor: "pointer",
    padding: 0,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    color: "#6b7280",
    borderBottom: "1px solid #e5e7eb",
    padding: "8px 10px",
  },
  td: { padding: "10px", borderBottom: "1px solid #f3f4f6", color: "#374151" },
  current: { background: "#fef2f2" },
  badge: {
    fontSize: 10,
    fontWeight: 700,
    color: "#fff",
    background: BRAND,
    borderRadius: 999,
    padding: "2px 8px",
    marginLeft: 8,
  },
  empty: {
    border: "1px dashed #d1d5db",
    borderRadius: 8,
    padding: "20px",
    textAlign: "center",
    fontSize: 13,
    color: "#6b7280",
  },
  quote: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "14px 16px",
    marginTop: 14,
    fontSize: 13,
    lineHeight: 1.9,
    color: "#374151",
  },
  quoteTotal: { fontWeight: 800, color: "#111827", fontSize: 15 },
};

const TERMS = [1, 2, 3, 4];

const blankForm = (year) => ({
  effective_from_year: year,
  effective_from_term: 1,
  price_per_lesson: "",
  material_fee_per_term: "",
  textbook_fee: "",
  textbook_covers_terms: 1,
  note: "",
});

const CourseFees = ({ courseId }) => {
  const thisYear = new Date().getFullYear();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(blankForm(thisYear));
  const [saving, setSaving] = useState(false);

  // preview
  const [pv, setPv] = useState({
    year: thisYear,
    term: 1,
    weeks: 10,
    subject_index: 1,
    has_sibling: false,
    discount_code: "",
    card_payment: false,
  });
  const [quote, setQuote] = useState(null);

  const load = async () => {
    if (!courseId) return;
    const res = await course_fee_list(courseId);
    setRows(Array.isArray(res?.data) ? res.data : []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // Which schedule is in force right now, so the table can mark it.
  const currentId = useMemo(() => {
    const now = rows
      .filter(
        (r) =>
          r.effective_from_year < pv.year ||
          (Number(r.effective_from_year) === Number(pv.year) &&
            Number(r.effective_from_term) <= Number(pv.term))
      )
      .sort(
        (a, b) =>
          b.effective_from_year - a.effective_from_year ||
          b.effective_from_term - a.effective_from_term
      )[0];
    return now?.id;
  }, [rows, pv.year, pv.term]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.price_per_lesson || Number(form.price_per_lesson) < 0) {
      toast.error("Enter a price per lesson");
      return;
    }
    setSaving(true);
    const res = await course_fee_save({ ...form, course_id: courseId });
    setSaving(false);
    if (res?.status) {
      setForm(blankForm(thisYear));
      load();
    }
  };

  const remove = async (id) => {
    const res = await course_fee_remove(id);
    if (res?.status) load();
  };

  const edit = (r) =>
    setForm({
      effective_from_year: r.effective_from_year,
      effective_from_term: r.effective_from_term,
      price_per_lesson: r.price_per_lesson,
      material_fee_per_term: r.material_fee_per_term,
      textbook_fee: r.textbook_fee,
      textbook_covers_terms: r.textbook_covers_terms,
      note: r.note || "",
    });

  const runQuote = async () => {
    const res = await course_fee_quote({
      course_id: courseId,
      year: pv.year,
      term: pv.term,
      weeks: pv.weeks,
      subject_index: pv.subject_index,
      has_sibling: pv.has_sibling ? 1 : "",
      discount_code: pv.discount_code,
      card_payment: pv.card_payment ? 1 : "",
    });
    if (res?.status) setQuote(res.data);
    else {
      setQuote(null);
      toast.error(res?.message || "Could not work out a price");
    }
  };

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div style={s.wrap}>
      <p style={s.intro}>
        Fees apply from the term you set here onward. To put a price up, add a
        new schedule for the term it starts — earlier terms keep charging what
        they always charged.
      </p>

      {/* existing schedules */}
      <div style={s.panel}>
        <p style={s.panelTitle}>Fee schedules</p>
        {rows.length === 0 ? (
          <div style={s.empty}>
            No fees set for this course yet. Add one below.
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Applies from</th>
                <th style={s.th}>Per lesson</th>
                <th style={s.th}>Material / term</th>
                <th style={s.th}>Textbook</th>
                <th style={s.th}>Covers</th>
                <th style={s.th}>Note</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={r.id === currentId ? s.current : null}>
                  <td style={s.td}>
                    {r.effective_from_year} T{r.effective_from_term}
                    {r.id === currentId && <span style={s.badge}>IN USE</span>}
                  </td>
                  <td style={s.td}>{money(r.price_per_lesson)}</td>
                  <td style={s.td}>{money(r.material_fee_per_term)}</td>
                  <td style={s.td}>{money(r.textbook_fee)}</td>
                  <td style={s.td}>
                    {r.textbook_covers_terms > 1
                      ? `${r.textbook_covers_terms} terms`
                      : "1 term"}
                  </td>
                  <td style={s.td}>{r.note || "—"}</td>
                  <td style={s.td}>
                    <button
                      type="button"
                      style={s.btnLink}
                      onClick={() => edit(r)}
                    >
                      Edit
                    </button>
                    {"  "}
                    <button
                      type="button"
                      style={{ ...s.btnLink, color: "#6b7280", marginLeft: 10 }}
                      onClick={() => remove(r.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* add / update */}
      <div style={s.panel}>
        <p style={s.panelTitle}>Add or update a schedule</p>
        <div style={s.grid}>
          <div style={s.field}>
            <label style={s.label}>Applies from year</label>
            <input
              type="number"
              style={s.input}
              value={form.effective_from_year}
              onChange={set("effective_from_year")}
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Applies from term</label>
            <select
              style={s.input}
              value={form.effective_from_term}
              onChange={set("effective_from_term")}
            >
              {TERMS.map((t) => (
                <option key={t} value={t}>
                  Term {t}
                </option>
              ))}
            </select>
          </div>
          <div style={s.field}>
            <label style={s.label}>Price per lesson *</label>
            <input
              type="number"
              step="0.01"
              style={s.input}
              value={form.price_per_lesson}
              onChange={set("price_per_lesson")}
              placeholder="79.00"
            />
            <span style={s.hint}>Tuition is weeks &times; this</span>
          </div>
          <div style={s.field}>
            <label style={s.label}>Material fee / term</label>
            <input
              type="number"
              step="0.01"
              style={s.input}
              value={form.material_fee_per_term}
              onChange={set("material_fee_per_term")}
              placeholder="30.00"
            />
            <span style={s.hint}>Never discounted</span>
          </div>
          <div style={s.field}>
            <label style={s.label}>Textbook fee</label>
            <input
              type="number"
              step="0.01"
              style={s.input}
              value={form.textbook_fee}
              onChange={set("textbook_fee")}
              placeholder="98.00"
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Textbook covers</label>
            <select
              style={s.input}
              value={form.textbook_covers_terms}
              onChange={set("textbook_covers_terms")}
            >
              <option value={1}>1 term</option>
              <option value={2}>2 terms</option>
              <option value={4}>4 terms (1 year)</option>
              <option value={8}>8 terms (2 years)</option>
            </select>
          </div>
          <div style={{ ...s.field, gridColumn: "1 / -1" }}>
            <label style={s.label}>Note</label>
            <input
              style={s.input}
              value={form.note}
              onChange={set("note")}
              placeholder="e.g. Y5/6 WRMT — Norwest 2026 T3"
            />
          </div>
        </div>
        <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
          <button type="button" style={s.btn} disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save schedule"}
          </button>
          <button
            type="button"
            style={s.btnGhost}
            onClick={() => setForm(blankForm(thisYear))}
          >
            Clear
          </button>
        </div>
      </div>

      {/* preview */}
      <div style={s.panel}>
        <p style={s.panelTitle}>Check a price</p>
        <div style={s.grid}>
          <div style={s.field}>
            <label style={s.label}>Year</label>
            <input
              type="number"
              style={s.input}
              value={pv.year}
              onChange={(e) => setPv({ ...pv, year: e.target.value })}
            />
          </div>
          <div style={s.field}>
            <label style={s.label}>Term</label>
            <select
              style={s.input}
              value={pv.term}
              onChange={(e) => setPv({ ...pv, term: e.target.value })}
            >
              {TERMS.map((t) => (
                <option key={t} value={t}>
                  Term {t}
                </option>
              ))}
            </select>
          </div>
          <div style={s.field}>
            <label style={s.label}>Weeks</label>
            <input
              type="number"
              style={s.input}
              value={pv.weeks}
              onChange={(e) => setPv({ ...pv, weeks: e.target.value })}
            />
            <span style={s.hint}>Sat 11, Tue/Wed 10</span>
          </div>
          <div style={s.field}>
            <label style={s.label}>Subject number</label>
            <input
              type="number"
              style={s.input}
              value={pv.subject_index}
              onChange={(e) => setPv({ ...pv, subject_index: e.target.value })}
            />
            <span style={s.hint}>3rd gets 10% off</span>
          </div>
          <div style={s.field}>
            <label style={s.label}>Discount</label>
            <select
              style={s.input}
              value={pv.discount_code}
              onChange={(e) => setPv({ ...pv, discount_code: e.target.value })}
            >
              <option value="">Automatic</option>
              <option value="early_bird">Early Bird (staff applied)</option>
              <option value="sibling">Sibling</option>
            </select>
          </div>
          <div style={s.field}>
            <label style={s.label}>Options</label>
            <div style={{ display: "flex", gap: 14, alignItems: "center", height: 38 }}>
              <label style={{ fontSize: 12, color: "#4b5563" }}>
                <input
                  type="checkbox"
                  checked={pv.has_sibling}
                  onChange={(e) => setPv({ ...pv, has_sibling: e.target.checked })}
                />{" "}
                Sibling
              </label>
              <label style={{ fontSize: 12, color: "#4b5563" }}>
                <input
                  type="checkbox"
                  checked={pv.card_payment}
                  onChange={(e) => setPv({ ...pv, card_payment: e.target.checked })}
                />{" "}
                Card (+1%)
              </label>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 14 }}>
          <button type="button" style={s.btn} onClick={runQuote}>
            Work out the price
          </button>
        </div>

        {quote && (
          <div style={s.quote}>
            <div>
              Tuition — {quote.weeks} &times; {money(quote.price_per_lesson)} ={" "}
              {money(quote.tuition)}
            </div>
            {quote.discount?.amount > 0 && (
              <div>
                {quote.discount.name} &minus;{quote.discount.percentage}% ={" "}
                &minus;{money(quote.discount.amount)}
              </div>
            )}
            {quote.material_fee > 0 && (
              <div>Material fee = {money(quote.material_fee)}</div>
            )}
            {quote.textbook_fee > 0 && (
              <div>Textbook = {money(quote.textbook_fee)}</div>
            )}
            {quote.card_surcharge > 0 && (
              <div>Card surcharge 1% = {money(quote.card_surcharge)}</div>
            )}
            <div style={s.quoteTotal}>Total {money(quote.total)}</div>
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              Using the schedule effective {quote.effective_from} &middot; all
              fees include GST
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseFees;
